'use client';

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { toast } from '@/lib/toast';
import { getDevItems, DevItem } from '@/services/api/devtracker';
import { ApiClientError } from '@/lib/api-client';
import { colors, typography } from '@/styles/theme';
import { DevtrackerGantt } from '@/features/devtracker/components/DevtrackerGantt';

export default function DevtrackerGanttPage() {
  const { t, locale } = useTranslation();
  const { isReady } = useAuthGuard();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<DevItem[]>([]);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getDevItems({ page: 1, pageSize: 100 });
      setItems(result.items);
    } catch (error) {
      if (error instanceof ApiClientError) {
        toast.error(error.message);
      } else {
        toast.error(t.devtracker?.toast?.loadFailed);
      }
    } finally {
      setLoading(false);
    }
  }, [t.devtracker?.toast?.loadFailed]);

  useEffect(() => {
    if (!isReady) return;
    loadItems();
  }, [isReady, loadItems]);

  if (!isReady) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-400">
        {t.devtracker?.loading}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <div className="bg-white border-b" style={{ borderColor: colors.border }}>
        <div className="px-6 h-12 flex items-center justify-between">
          <h1 className={typography.h4}>{t.devtracker?.gantt?.title}</h1>
          <div />
        </div>
      </div>

      <div className="px-6 space-y-4">
        <p className="text-sm text-gray-500">{t.devtracker?.gantt?.subtitle}</p>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          {loading ? (
            <div className="text-center text-gray-400">{t.devtracker?.loading}</div>
          ) : (
            <DevtrackerGantt items={items} locale={locale} t={t} />
          )}
        </div>
      </div>
    </div>
  );
}
